import { findVenue, getTown } from '@wyn/content';
import type { PlayerSnapshot } from '@wyn/contracts';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { UI } from '../icons/ui-icons.ts';
import { formatDuration, useNow } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { BAR_META } from './BarMeter.tsx';
import { Icon } from './Icon.tsx';

type Board = NonNullable<PlayerSnapshot['board']>;
type Task = Board['tasks'][number];

const anchor = (id: string, label: string) => <a href={`#${id}`} className="text-gilt hover:underline">{label}</a>;

/** "40/100 Focus", "1/2 wins". */
export const taskProgress = (t: Task) => `${Math.floor(t.progress)}/${t.target} ${t.unit === 'wins' ? 'wins' : BAR_META[t.unit].label}`;

/** Where a task is done, from where the player stands: a venue here, the road to its town, or a section of this page. */
function where(t: Task, p: PlayerSnapshot): ReactNode {
  if (t.venue) {
    const v = findVenue(t.venue)!;
    return v.town === p.town
      ? <Link to="/venue/$venueKey" params={{ venueKey: v.key }} className="text-gilt hover:underline">To {v.name}</Link>
      : anchor('roads', `Take the road to ${getTown(v.town).name}`);
  }
  switch (t.key) {
    case 'train': return anchor('training-yard', 'To the Training Yard');
    case 'fights': return anchor('folk', 'To the folk about town');
    case 'crimes': return anchor('back-alleys', 'To the back alleys');
    default: return null;
  }
}

/** When the day's three give way to a new three, as a countdown. */
function TimeLeft({ endsAt }: { endsAt: number }) {
  const now = useNow();
  const { skew } = useMe();
  return <>{formatDuration(endsAt - (now + skew))}</>;
}

/** The day's three tasks, in every town: progress on each, where it's done, and what finishing all three pays. */
export function NoticeBoard({ player: p }: { player: PlayerSnapshot }) {
  const board = p.board;
  if (!board) return null;
  const doneCount = board.tasks.filter((t) => t.done).length;
  return (
    <section id="notice-board" className="panel scroll-mt-4 space-y-3" aria-labelledby="notice-board-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="notice-board-heading" className="heading"><Icon name={UI.noticeBoard} className="mr-1 text-gilt" />Notice board</h2>
        <span className="font-mono text-sm text-ink-faint" data-testid="notice-count">{doneCount} of {board.tasks.length}</span>
      </div>
      <p className="text-sm text-ink-dim">
        {board.done
          ? <>All done for today. +{board.bonus} Renown paid.</>
          : <>Each notice pays +{board.reward} Renown, and all three pay +{board.bonus} more.</>}
        {board.streak > 0 && <> <span className="text-gilt" data-testid="notice-streak">{board.streak} {board.streak === 1 ? 'day' : 'days'} running.</span></>}
        {' '}<span className="whitespace-nowrap text-ink-faint">New notices in <TimeLeft endsAt={board.endsAt} />.</span>
      </p>
      <ul className="space-y-3">
        {board.tasks.map((t) => (
          <li key={t.key} data-testid="notice-task" data-task={t.key} data-done={t.done} className="space-y-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className={t.done ? 'text-ink-dim line-through decoration-rule' : ''}>
                <Icon name={BAR_META[t.bar].icon} className="mr-1 text-ink-faint" />
                {t.name}
                {t.done && <span className="sr-only"> (done)</span>}
              </span>
              <span className={`font-mono text-xs ${t.done ? 'text-verdigris' : 'text-ink-faint'}`}>{t.done ? '✓' : taskProgress(t)}</span>
            </div>
            <div className="h-1.5 w-full bg-rule" role="meter" aria-label={t.name} aria-valuenow={t.progress} aria-valuemin={0} aria-valuemax={t.target}>
              <div className={`h-full ${t.done ? 'bg-verdigris' : BAR_META[t.bar].color}`} style={{ width: `${Math.min(100, (t.progress / t.target) * 100)}%` }} />
            </div>
            {!t.done && (
              <div className="text-sm text-ink-dim">
                {t.hint} <span className="whitespace-nowrap">{where(t, p)}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The day's three in the sidebar, on every page. */
export function NoticeSummary({ player: p }: { player: PlayerSnapshot }) {
  const board = p.board;
  if (!board) return null;
  return (
    <div className="panel space-y-2" data-testid="notice-summary">
      <Link to="/town" hash="notice-board" className="smallcaps flex items-baseline justify-between text-sm text-ink-dim hover:text-gilt">
        <span><Icon name={UI.noticeBoard} className="mr-1" />Today’s notices</span>
        {board.streak > 1 && <span className="font-mono text-xs text-gilt">{board.streak} days</span>}
      </Link>
      <ul className="space-y-1 text-sm">
        {board.tasks.map((t) => (
          <li key={t.key} className="flex items-baseline justify-between gap-2">
            <span className={`truncate ${t.done ? 'text-ink-faint line-through decoration-rule' : ''}`}>{t.name}</span>
            <span className={`shrink-0 font-mono text-xs ${t.done ? 'text-verdigris' : 'text-ink-faint'}`}>{t.done ? '✓' : `${Math.floor(t.progress)}/${t.target}`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
